import { useState, useRef, useEffect } from 'react';
import { Users, X, Check } from 'lucide-react';
import { useSpace, SpaceMember } from '../../contexts/SpaceContext';

interface SpaceMembersProps {
  collapsed?: boolean;
}

export default function SpaceMembers({ collapsed }: SpaceMembersProps) {
  const {
    members,
    membersLoading,
    filterByMember,
    setFilterByMember,
    isPersonalSpace,
    currentSpace,
  } = useSpace();

  // Keep track of previous members to show during transitions
  const prevMembersRef = useRef<typeof members>(members);
  const prevSpaceIdRef = useRef<string | null>(currentSpace?.id ?? null);

  // Update ref when we have valid members data
  useEffect(() => {
    if (members.length > 0) {
      prevMembersRef.current = members;
      prevSpaceIdRef.current = currentSpace?.id ?? null;
    }
  }, [members, currentSpace?.id]);

  // Don't show in personal space
  if (isPersonalSpace) {
    return null;
  }

  // Use current members if available, otherwise use previous members during loading
  // But only use previous members if we're loading for the same or a different space (transition)
  const displayMembers = members.length > 0 ? members :
    (membersLoading && prevMembersRef.current.length > 0 && prevSpaceIdRef.current !== currentSpace?.id)
      ? [] // Different space, don't show stale data
      : membersLoading ? prevMembersRef.current : [];

  // Show loading only on initial load when we have no previous data
  if (membersLoading && displayMembers.length === 0 && prevMembersRef.current.length === 0) {
    return (
      <div className="px-2 py-2">
        <div className="text-xs text-[var(--content-tertiary)]">Loading members...</div>
      </div>
    );
  }

  // No members to show
  if (displayMembers.length === 0) {
    return null;
  }

  // Use displayMembers instead of members in the render

  // Collapsed view - just show avatars
  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-2 py-2">
        {displayMembers.slice(0, 5).map((member) => (
          <MemberAvatar
            key={member.user_id}
            member={member}
            isSelected={filterByMember === member.user_id}
            onClick={() => {
              if (filterByMember === member.user_id) {
                setFilterByMember(null);
              } else {
                setFilterByMember(member.user_id);
              }
            }}
            size="sm"
          />
        ))}
        {displayMembers.length > 5 && (
          <div className="w-7 h-7 rounded-full bg-[var(--surface-tertiary)] flex items-center justify-center text-xs text-[var(--content-tertiary)]">
            +{displayMembers.length - 5}
          </div>
        )}
      </div>
    );
  }

  // Expanded view
  return (
    <div className="mt-4 pt-4 border-t border-[var(--border-primary)]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1 text-xs font-medium text-[var(--content-secondary)] uppercase tracking-wider">
          <Users className="w-3 h-3" />
          Members
        </div>
        {filterByMember && (
          <button
            onClick={() => setFilterByMember(null)}
            className="text-xs text-[var(--accent-primary)] hover:underline flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Clear filter
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {displayMembers.map((member) => (
          <MemberAvatar
            key={member.user_id}
            member={member}
            isSelected={filterByMember === member.user_id}
            onClick={() => {
              if (filterByMember === member.user_id) {
                setFilterByMember(null);
              } else {
                setFilterByMember(member.user_id);
              }
            }}
          />
        ))}
      </div>

      {filterByMember && (
        <div className="mt-2 text-xs text-[var(--content-secondary)] bg-[var(--accent-subtle)] px-2 py-1 rounded">
          Showing events by {displayMembers.find(m => m.user_id === filterByMember)?.display_name || 'member'}
        </div>
      )}
    </div>
  );
}

// Member Avatar Component
interface MemberAvatarProps {
  member: SpaceMember;
  isSelected: boolean;
  onClick: () => void;
  size?: 'sm' | 'md';
  showName?: boolean;
}

function MemberAvatar({ member, isSelected, onClick, size = 'md', showName }: MemberAvatarProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const sizeClasses = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-8 h-8 text-sm';
  const displayName = member.display_name || member.email?.split('@')[0] || 'User';
  const initial = displayName.charAt(0).toUpperCase();

  // Generate a consistent color based on user_id
  const colors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-emerald-500',
    'bg-orange-500',
    'bg-cyan-500',
    'bg-rose-500',
    'bg-amber-500',
  ];
  const colorIndex = member.user_id.charCodeAt(0) % colors.length;
  const bgColor = colors[colorIndex];

  return (
    <div className="relative">
      <button
        onClick={onClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`
          relative flex items-center font-medium transition-all
          ${showName
            ? 'gap-2 px-1 py-1 rounded-full hover:bg-[var(--sidebar-hover)]'
            : `${sizeClasses} rounded-full justify-center`
          }
          ${isSelected && !showName
            ? 'ring-2 ring-[var(--accent-primary)] ring-offset-2 ring-offset-[var(--sidebar-bg)]'
            : !showName ? 'hover:ring-2 hover:ring-[var(--border-primary)]' : ''
          }
        `}
        title={`${displayName}${member.is_owner ? ' (Owner)' : ` (${member.role})`}`}
      >
        {member.avatar_url ? (
          <img
            src={member.avatar_url}
            alt={displayName}
            className={`rounded-full object-cover flex-shrink-0 ${sizeClasses}`}
          />
        ) : (
          <div className={`${bgColor} text-white rounded-full flex items-center justify-center flex-shrink-0 ${sizeClasses}`}>
            {initial}
          </div>
        )}

        {showName && (
          <span className={`text-sm truncate max-w-[80px] ${isSelected ? 'text-[var(--accent-primary)] font-medium' : 'text-[var(--content-primary)]'}`}>
            {displayName}
          </span>
        )}

        {/* Selection indicator - only show on avatar for collapsed view */}
        {isSelected && !showName && (
          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[var(--accent-primary)] rounded-full flex items-center justify-center">
            <Check className="w-2.5 h-2.5 text-white" />
          </div>
        )}

        {/* Selection indicator for expanded view with name */}
        {isSelected && showName && (
          <Check className="w-4 h-4 text-[var(--accent-primary)] flex-shrink-0" />
        )}

        {/* Role badge for owner */}
        {member.is_owner && !showName && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full border border-white" title="Owner" />
        )}
      </button>

      {/* Tooltip - only for collapsed avatars without name */}
      {showTooltip && !showName && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded shadow-lg text-xs text-[var(--content-primary)] whitespace-nowrap z-50">
          {displayName}
          {member.is_owner && <span className="text-amber-500 ml-1">(Owner)</span>}
          {!member.is_owner && <span className="text-[var(--content-tertiary)] ml-1">({member.role})</span>}
        </div>
      )}
    </div>
  );
}
